/**
 * advance-extractors.ts — per-tick income from Constructor-built resource
 * extractors (M14). Asteroid Mining Outposts add Minerals; Gas Giant
 * Refineries add Fuel. Flat per-structure rate in v1; class-ladder
 * multipliers are a future polish pass.
 *
 * Runs AFTER advancePopulationAndEconomy so the income lands on the same
 * tick as the rest of the economy pass, and before advanceStructures so
 * Mineral/Fuel caps (from storage structures) are still consulted
 * accurately — we need to respect the empire caps the same way
 * advancePopulationAndEconomy does.
 */

import {
  asteroidMiningMineralsPerTick,
  gasGiantRefineryFuelPerTick,
} from './balance-space-structures';
import { resourceCapsForEmpire } from './planet-economy';
import type { Empire, GameState } from '../state/types/index';

/** Add extractor income to each empire's resource pools, respecting caps. */
export function advanceSpaceExtractors(state: GameState): void {
  // Accumulate deltas per empire so we fold + clamp once at the end,
  // matching the per-empire pattern in advancePopulationAndEconomy.
  const deltas = new Map<Empire, { minerals: number; fuel: number }>();
  const getDelta = (empire: Empire): { minerals: number; fuel: number } => {
    let d = deltas.get(empire);
    if (!d) {
      d = { minerals: 0, fuel: 0 };
      deltas.set(empire, d);
    }
    return d;
  };

  const sd = state.structuresData;
  const mineralsPerTick = asteroidMiningMineralsPerTick(sd);
  const fuelPerTick = gasGiantRefineryFuelPerTick(sd);
  for (const ship of state.ships.values()) {
    if (ship.currentHull <= 0) continue;
    if (ship.systemId === null) continue; // mid-jump extractors don't produce
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    if (ship.chassis === 'Asteroid Mining Outpost') {
      getDelta(empire).minerals += mineralsPerTick;
    } else if (ship.chassis === 'Gas Giant Refinery') {
      getDelta(empire).fuel += fuelPerTick;
    }
  }

  for (const [empire, d] of deltas) {
    const caps = resourceCapsForEmpire(state, empire.id);
    if (d.minerals > 0) {
      empire.resources.minerals = Math.min(
        caps.minerals,
        empire.resources.minerals + d.minerals,
      );
    }
    if (d.fuel > 0) {
      empire.resources.fuel = Math.min(
        caps.fuel,
        empire.resources.fuel + d.fuel,
      );
    }
  }
}
